import Big from 'big.js';
import {QtyType} from '@/enum/order';
import {numUtil} from '@/helper/num';
import {updateSizeStatus, updateSizePercent} from './calc';
import type {BookItem, Book} from '@/types';

export function convertContractNewOrderBook(
  tradePair,
  bidCache,
  bidPriceEps,
  bidCacheRaw,
  bidPriceEpsRaw,
  askCache,
  askPriceEps,
  askCacheRaw,
  askPriceEpsRaw,
  caches,
  qtyType
): Book {
  const {symbol, valuePrecision, contractSizePrecision} = tradePair;
  const BID = 'bid';
  const ASK = 'ask';
  const bids = bidPriceEps.map((priceEp, i) =>
    convertOrder(priceEp, bidCache.get(priceEp), i, BID, qtyType, tradePair)
  );
  const asks = askPriceEps.map((priceEp, i) =>
    convertOrder(priceEp, askCache.get(priceEp), i, ASK, qtyType, tradePair)
  );
  const bidsRaw = bidPriceEpsRaw.map((priceEp, i) =>
    convertOrder(priceEp, bidCacheRaw.get(priceEp), i, BID, qtyType, tradePair)
  );
  const asksRaw = askPriceEpsRaw.map((priceEp, i) =>
    convertOrder(priceEp, askCacheRaw.get(priceEp), i, ASK, qtyType, tradePair)
  );

  const now = Date.now();
  const {bidSizeCache, askSizeCache, bidTimeCache, askTimeCache, bidStatusCache, askStatusCache} = caches;

  updateSizeStatus(bids, now, bidSizeCache, bidTimeCache, bidStatusCache);
  updateSizeStatus(asks, now, askSizeCache, askTimeCache, askStatusCache);

  updateTotalBySize(bids, asks, qtyType, valuePrecision, contractSizePrecision);

  return {
    reset: false,
    symbol,
    bids,
    asks,
    bid1Ep: bids[0]?.priceEp ?? 0,
    bid1EpRaw: bidsRaw[0]?.priceEp ?? 0,
    ask1Ep: asks[0]?.priceEp ?? 0,
    ask1EpRaw: asksRaw[0]?.priceEp ?? 0,
  };
}

function convertOrder(priceEp, size, index, side, qtyType, tradePair): BookItem {
  const {
    pricePrecision,
    priceScale,
    contractSide,
    contractSize,
    valuePrecision,
    contractSizePrecision,
    qtyPrecision,
    priceFactor,
  } = tradePair;
  const sizeValue = calcSizeValue(size, priceEp, qtyType, contractSide, contractSize, priceFactor);
  const sizeStr = calcSizeValueStr(sizeValue, qtyType, valuePrecision, contractSizePrecision, qtyPrecision);
  return {
    priceEp,
    price: numUtil.delimit(numUtil.floor(priceEp, pricePrecision, priceScale)),
    size: sizeValue,
    sizeStr,
    total: 0,
    totalStr: '0',
    percent: '0',
    [side]: true,
    odd: index % 2 !== 0,
    ss: 0,
    rowSize: size,
  };
}

function updateTotalBySize(bids, asks, qtyType, valuePrecision, contractSizePrecision) {
  const bidTotal = bids.reduce((p, c) => sizeTotalReducer(p, c, qtyType, valuePrecision, contractSizePrecision), 0);
  const askTotal = asks.reduce((p, c) => sizeTotalReducer(p, c, qtyType, valuePrecision, contractSizePrecision), 0);

  const total = Math.max(bidTotal, askTotal);
  updateSizePercent(bids, total);
  updateSizePercent(asks, total);
}

function sizeTotalReducer(
  p: number,
  c: BookItem,
  qtyType: string,
  valuePrecision: number,
  contractSizePrecision: number
): number {
  const total = Number(Big(c.size).plus(p));
  let precision = 2;
  if (qtyType === QtyType.Cont) {
    precision = total > 1e3 ? 2 : 0;
  }
  if (qtyType === QtyType.Base) {
    precision = valuePrecision;
  }
  if (qtyType === QtyType.Coin) {
    precision = total > 1e3 ? 2 : contractSizePrecision;
  }
  c.total = total;
  c.totalStr = numUtil.abbreviateNumber(total, precision);
  return total;
}

function calcSizeValueStr(
  sizeValue: number,
  qtyType: string,
  valuePrecision: number,
  contractSizePrecision: number,
  qtyPrecision: number
): string {
  if (qtyType === QtyType.Cont) {
    const precision = sizeValue > 1e3 ? 2 : qtyPrecision;
    return numUtil.abbreviateNumber(sizeValue, precision);
  }
  if (qtyType === QtyType.Base) {
    return numUtil.abbreviateNumber(sizeValue, valuePrecision);
  }
  // only consider contract side greater than 0 trade pair
  if (qtyType === QtyType.Coin) {
    const precision = sizeValue > 1e3 ? 2 : contractSizePrecision;
    return numUtil.abbreviateNumber(sizeValue, precision);
  }
  return '0';
}

function calcSizeValue(
  size: number,
  priceEp: number,
  qtyType: string,
  contractSide: number,
  contractSize: number,
  priceFactor: number
): number {
  if (qtyType === QtyType.Cont) {
    return size;
  }
  if (qtyType === QtyType.Base) {
    return calcValue(size, contractSide, contractSize, priceEp, priceFactor);
  }
  // only consider contract side greater than 0 trade pair
  if (qtyType === QtyType.Coin) {
    return Number(Big(size).times(contractSize));
  }
  return 0;
}

function calcValue(
  size: number,
  contractSide: number,
  contractSize: number,
  priceEp: number,
  priceFactor: number = 0
): number {
  const sizeBig = Big(size).times(contractSize);
  return Number(contractSide > 0 ? sizeBig.times(priceEp).div(priceFactor) : sizeBig.times(priceFactor).div(priceEp));
}
